import React from 'react';
import { PortfolioStats } from '../../types';
import { formatPercent } from '../../utils/formatters';
import { useCurrency } from '../../context/CurrencyContext';
import { InfoTooltip } from '../ui/InfoTooltip';

interface DashboardProps {
  stats: PortfolioStats;
  loading: boolean;
  /** Zero-real-transactions state: show muted placeholder values + next-step subtext. */
  empty?: boolean;
}

export function Dashboard({ stats, loading, empty }: DashboardProps) {
  const { fmt, fmtDisplay } = useCurrency();
  const pnlPositive  = stats.display.totalPnl >= 0;
  const changePositive = stats.change24h >= 0;

  // Empty state: keep all four cards, muted placeholder values, and a subtext
  // line that points at the next action instead of apologising for being empty.
  const items = empty
    ? [
        { label: 'Total Value',  value: fmt(0), valueColor: 'text-slate-100', sub: 'Add a transaction to see this', subColor: 'text-slate-500' },
        { label: 'All-time P&L', value: '—',    valueColor: 'text-slate-500', sub: 'Tracked from your first buy',   subColor: 'text-slate-500' },
        { label: '24h Change',   value: '—',    valueColor: 'text-slate-500', sub: 'Live once you hold something',  subColor: 'text-slate-500' },
        { label: 'Assets',       value: '0',    valueColor: 'text-slate-100', sub: 'Unlimited on the free plan',    subColor: 'text-slate-500' },
      ]
    : [
    {
      label:      'Total Value',
      value:      fmt(stats.totalValue),
      valueColor: 'text-slate-100',
      sub:        stats.totalCost > 0 ? `Cost basis ${fmtDisplay(stats.display.totalCost)}` : null,
      subColor:   'text-slate-500',
    },
    {
      label:      'All-time P&L',
      value:      fmtDisplay(stats.display.totalPnl),
      valueColor: pnlPositive ? 'text-emerald-400' : 'text-red-400',
      sub:        stats.totalCost > 0 ? formatPercent(stats.display.totalPnlPercent) : null,
      subColor:   pnlPositive ? 'text-emerald-400' : 'text-red-400',
      // Cost is historical and value is current, so the gap between them carries
      // currency movement as well as price movement. Disclosed rather than hidden:
      // a figure that moves on a day the asset did not is otherwise inexplicable.
      tip: `Cost is converted at the exchange rate for each transaction's own date; current value at today's rate. The difference therefore includes currency movement as well as price movement.`,
    },
    {
      label:      '24h Change',
      value:      fmtDisplay(stats.display.change24hValue),
      valueColor: changePositive ? 'text-emerald-400' : 'text-red-400',
      sub:        formatPercent(stats.change24h),
      subColor:   changePositive ? 'text-emerald-400' : 'text-red-400',
    },
    {
      label:      'Assets',
      value:      String(stats.assetCount),
      valueColor: 'text-slate-100',
      sub:        stats.assetCount > 0
        ? `${stats.assetCount} position${stats.assetCount !== 1 ? 's' : ''}`
        : 'No holdings yet',
      subColor:   'text-slate-500',
    },
  ];

  // Spec: rgba(255,255,255,0.025) bg, 0.5px hairline border, 10px radius, 14×16 padding.
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'rgb(var(--overlay) / 0.025)',
    border: '0.5px solid rgb(var(--overlay) / 0.06)',
    borderRadius: 10,
    padding: '14px 16px',
  };

  if (loading && stats.totalValue === 0 && !empty) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((_, i) => (
          <div key={i} className="animate-pulse" style={cardStyle}>
            <div className="h-2 bg-slate-800 rounded w-14 mb-3" />
            <div className="h-4 bg-slate-800 rounded w-24" />
          </div>
        ))}
      </div>
    );
  }

  // Shared cell content (label · value · sub) — identical on mobile and desktop.
  const renderStat = (item: typeof items[number]) => (
    <>
      <p
        className="uppercase mb-1.5"
        style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', color: 'var(--color-stat-label)' }}
      >
        {/* inline-flex so the 14px icon sits centred against the 10px label rather
            than riding above its baseline. Matches TaxView's StatCard labels. */}
        <span className="inline-flex items-center gap-1.5">
          {item.label}
          {item.tip ? <InfoTooltip text={item.tip} /> : null}
        </span>
      </p>
      <p
        className={`leading-tight ${item.valueColor}`}
        style={{ fontSize: 19, fontWeight: 500, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}
      >
        {item.value}
      </p>
      {item.sub && (
        <p className={`text-[11px] mt-1.5 opacity-80 ${item.subColor}`}>{item.sub}</p>
      )}
    </>
  );

  return (
    <div>
      {/* Mobile (< lg): boxless 2×2 grid quartered by thin dividers — matches the
          mobile holdings list (border-slate-700/40). */}
      <div className="grid grid-cols-2 lg:hidden">
        {items.map((item, i) => {
          const col = i % 2;         // 0 = left column, 1 = right column
          const row = Math.floor(i / 2); // 0 = top row, 1 = bottom row
          return (
            <div
              key={i}
              className={`py-4 ${col === 0 ? 'pr-4 border-r border-slate-700/40' : 'pl-4'} ${row === 0 ? 'border-b border-slate-700/40' : ''}`}
            >
              {renderStat(item)}
            </div>
          );
        })}
      </div>

      {/* Desktop (≥ lg): unchanged boxed cards */}
      <div className="hidden lg:grid grid-cols-4 gap-3">
        {items.map((item, i) => (
          <div key={i} style={cardStyle}>
            {renderStat(item)}
          </div>
        ))}
      </div>
    </div>
  );
}
